import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Constants from 'assets/utils/constant';
import Icons from 'components/atoms/Icons';
import Image from 'next/image';
import {
  LatLng,
  LocationFields,
  LocationResultType,
} from 'components/types/MapTypes';
import { formatSearchResultUrl } from 'assets/utils/formatUrl';
import { getMapsLink } from './LocationMap';

type LocationResultProps = LocationResultType & {
  keyProp: string;
  location: LocationFields;
  origin: LatLng;
  viewMode: string;
};

const LocationResult = (props: LocationResultProps) => {
  const { origin } = props;
  const {
    city,
    phone,
    state,
    street1,
    street2,
    zip,
    epicdeptid,
    epicproviderid,
    latitude,
    longitude,
    title,
    image,
    link,
  } = props.location;

  const [walkInWaitTime, setWalkInWaitTime] = useState<null | string>(null);
  const [nextAppointmentTime, setNextAppointmentTime] = useState<null | string>(
    null
  );

  useEffect(() => {
    const shouldFetchWaitTimes =
      title?.includes('OnCall Urgent') || title?.includes('PromptCare');
    if (shouldFetchWaitTimes && epicdeptid) {
      const fetchWaitTimes = async () => {
        try {
          const waitTimesResponse = await axios.post(
            Constants.api.epicApi + Constants.api.getDepartmentWaitTimes,
            {
              Departments: [
                {
                  ID: epicdeptid,
                  Type: 'External',
                },
              ],
              MinutesToLookBack_: 60,
              UseAppointmentTimeIfAfterStartEvent_: true,
              CalculateForOverallLevel_: false,
              CalculateForEachDepartment_: false,
              StartEvent: { ID: '', Type: '' },
              EndEvent: { ID: '', Type: '' },
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Epic-Client-ID': Constants.api.epicClientID,
                Accept: 'application/json',
                Authorization: `Basic ${Constants.api.epicAccessToken}`,
              },
            }
          );
          const waitTimeSeconds = waitTimesResponse.data.AverageWaitTime;
          const waitTimeMinutes = Math.floor(waitTimeSeconds / 60);
          const waitTimeHours = Math.floor(waitTimeMinutes / 60);
          const remainingMinutes = waitTimeMinutes % 60;

          let formattedWaitTime = '';
          if (waitTimeHours > 0) {
            formattedWaitTime = `${waitTimeHours} Hour${
              waitTimeHours > 1 ? 's' : ''
            } and ${remainingMinutes} Minute${remainingMinutes > 1 ? 's' : ''}`;
          } else {
            formattedWaitTime = `${waitTimeMinutes} Minute${waitTimeMinutes > 1 ? 's' : ''}`;
          }

          setWalkInWaitTime(formattedWaitTime);

          const today = new Date().toISOString().split('T')[0];
          const tomorrow = new Date(Date.now() + 86400000)
            .toISOString()
            .split('T')[0];

          if (!title?.includes('PromptCare')) {
            const nextAppointmentResponse = await axios.post(
              Constants.api.epicApi + Constants.api.getScheduleDays,
              {
                StartDate: today,
                EndDate: tomorrow,
                ProviderID: epicproviderid,
                ProviderIDType: 'External',
                VisitTypeIDs: [{ ID: '24692', Type: 'External' }],
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Epic-Client-ID': Constants.api.epicClientID,
                  Accept: 'application/json',
                  Authorization: `Basic ${Constants.api.epicAccessToken}`,
                },
              }
            );
            const scheduleDays = nextAppointmentResponse.data.ScheduleDays;
            let nextAppointment = null;
            let nextAppointmentDate = null;

            for (const day of scheduleDays) {
              if (day.Slots.length > 0) {
                nextAppointment = day.Slots[0];
                nextAppointmentDate = day.Date;
                break;
              }
            }

            if (nextAppointment) {
              const appointmentTime = new Date(
                `${nextAppointmentDate}T${nextAppointment.Time}`
              );
              const hours = appointmentTime.getHours();
              const minutes = appointmentTime.getMinutes();
              const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
              const formattedHours = hours % 12 || 12;
              const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
              const formattedDate =
                nextAppointmentDate === today ? 'Today' : 'Tomorrow';
              setNextAppointmentTime(
                `${formattedDate} at ${formattedHours}:${formattedMinutes} ${ampm}`
              );
            } else {
              setNextAppointmentTime('No upcoming appointments');
            }
          }
        } catch (error) {
          console.error(
            'Error fetching wait times or next appointment:',
            error
          );
        }
      };

      fetchWaitTimes();
    }
  }, [name, epicdeptid, epicproviderid]);

  const shouldDisplayBlock =
    title?.includes('OnCall Urgent') ||
    (title?.includes('PromptCare') && walkInWaitTime);

  const getDistance = (
    originLat: number,
    originLon: number,
    latitude: number,
    longitude: number
  ) => {
    const EarthMeanRadiusInKilometers = 6371.0087714;
    const φ1 = (originLat * Math.PI) / 180;
    const φ2 = (latitude * Math.PI) / 180;
    const Δφ = ((latitude - originLat) * Math.PI) / 180;
    const Δλ = ((longitude - originLon) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distanceInMiles = EarthMeanRadiusInKilometers * c * 0.621371192;

    return distanceInMiles;
  };

  const distance =
    origin.latitude &&
    origin.longitude &&
    getDistance(
      origin.latitude,
      origin.longitude,
      parseFloat(latitude),
      parseFloat(longitude)
    );

  return (
    <div
      className={`search-result location-result`}
      itemScope
      itemType="http://schema.org/Physician"
      key={props.keyProp}
      id={props.keyProp}
    >
      <a
        className="card-link"
        href={formatSearchResultUrl(link)}
        aria-label={`Profile for ${title}`}
      ></a>
      <div className="image-container" key={title}>
        {distance && (
          <div className="distance osf-label">{distance.toFixed(2)} mi</div>
        )}
        <Image
          itemProp="image"
          src={image ? image : Constants.fallbackImages.location}
          alt={title}
          fill
          objectFit="cover"
        />
      </div>
      <div className="result-info">
        <div
          className="location-address"
          itemProp="hospitalAffiliation"
          itemType="http://schema.org/Hospital"
        >
          <h3 className="btn-tertiary result-name" itemProp="name">
            {title}
          </h3>
        </div>

        <div className="result-details">
          <div
            className="result-detail"
            itemProp="address"
            itemScope
            itemType="http://schema.org/PostalAddress"
          >
            <Icons id="pld-location-pin" />
            <p>
              <a
                className="track-link-click"
                href={getMapsLink({
                  street1,
                  street2,
                  city,
                  state,
                  zip,
                })}
                target="_blank"
                rel="noreferrer"
              >
                {street1}, {`${street2 ? street2 + ', ' : ''}`} {city}, {state}{' '}
                {zip}
              </a>
            </p>
          </div>
          {phone && (
            <div className="result-detail">
              <Icons id="pld-phone" />
              <p className="label-phone">
                <a href={`tel:${phone}`}>{phone}</a>
              </p>
            </div>
          )}
        </div>
        {shouldDisplayBlock && (
          <div className="epic-wait-time">
            <ul>
              {walkInWaitTime && (
                <li>
                  <Icons id="pld-walk-in" className="icon" />
                  <span>
                    <span className="label">Walk-in wait time: </span>
                    <span id="walk-in-wait-time" className="walk-in-wait-time">
                      {walkInWaitTime}
                    </span>
                  </span>
                </li>
              )}

              {!title?.includes('PromptCare') && nextAppointmentTime && (
                <li>
                  <Icons id="pld-calendar" className="icon" />
                  <span>
                    <span className="label">Next appointment: </span>
                    <span id="next-appt-time" className="walk-in-wait-time">
                      {nextAppointmentTime}
                    </span>
                  </span>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationResult;
